/**
 * "Refazer análise morfológica" na seção de fotografias da pré-análise (E184.F4.C01, GUS-145 item 5,
 * ADR-0136 §4).
 *
 * A E184.F4 pôs o botão em `ConferenciaFacial`, que nenhuma página monta desde a E123.F3 (a
 * pré-análise mostra a conferência por `FotografiasDoCaso`): no QA de 29/10 só apareciam "Ajustar
 * recorte" e "Trocar fotos". O botão mora agora onde o parecer aparece. Com parecer gravado, chama a
 * mesma comparação sobre o par eleito, mesmo com documento concluído; o servidor grava o parecer
 * novo, registra o anterior na trilha e refaz o exame e o relatório de riscos sem clique.
 *
 * E200.F2 (GUS-220, ADR-0143 §3): com a análise do caso já em curso no servidor (409), o botão não
 * disparou nada: a seção diz que a análise está em andamento e relê, para esperar por ela.
 */
import { useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { executarConferenciaFacial } from '@/lib/conferencia_facial';
import { ExameEletronicoError } from '@/lib/exame_eletronico_erros';

const t = ptBR.exameEletronico.conferenciaFacial;

export interface RefazerAnaliseMorfologicaProps {
  caseId: string;
  /** A análise em segundo plano ainda roda: refazer agora disputaria com ela. */
  emAndamento: boolean;
  /** O parecer novo foi gravado: a seção relê e avisa. */
  onRefeita: () => void;
  /** E200.F2: o servidor já tinha a análise em curso; a seção relê para esperar por ela. */
  onEmAndamento?: () => void;
  /**
   * E206.F2 (GUS-296 item 2): o caso aguarda arquivos — o botão fica indisponível e a seção diz o
   * que falta; a recusa do servidor (409 `AGUARDANDO_ARQUIVOS`) diz o mesmo.
   */
  aguardando?: boolean;
}

export function RefazerAnaliseMorfologica({
  caseId,
  emAndamento,
  onRefeita,
  onEmAndamento,
  aguardando = false,
}: RefazerAnaliseMorfologicaProps): JSX.Element {
  const client = useApiClient();
  const [refazendo, setRefazendo] = useState(false);
  const [erro, setErro] = useState(false);
  const [jaEmCurso, setJaEmCurso] = useState(false);
  const [recusadoPorFalta, setRecusadoPorFalta] = useState(false);

  const refazer = (): void => {
    void (async () => {
      setRefazendo(true);
      setErro(false);
      setJaEmCurso(false);
      try {
        await executarConferenciaFacial(client, caseId);
        onRefeita();
      } catch (err) {
        if (err instanceof ExameEletronicoError && err.reason === 'conferencia_em_andamento') {
          setJaEmCurso(true);
          onEmAndamento?.();
        } else if (err instanceof ExameEletronicoError && err.reason === 'aguardando_arquivos') {
          setRecusadoPorFalta(true);
          onEmAndamento?.();
        } else {
          setErro(true);
        }
      } finally {
        setRefazendo(false);
      }
    })();
  };

  return (
    <div className="space-y-sc-2">
      <Button
        type="button"
        variant="secondary"
        disabled={refazendo || emAndamento || aguardando || recusadoPorFalta}
        onClick={refazer}
        data-testid="fotografias-refazer"
      >
        {refazendo ? t.conferindo : t.refazer}
      </Button>
      {erro ? (
        <p
          role="alert"
          className="text-fg-danger text-body-sm"
          data-testid="fotografias-refazer-erro"
        >
          {t.erroRefazer}
        </p>
      ) : null}
      {recusadoPorFalta && !aguardando ? (
        <p
          role="status"
          className="text-body-sm text-warning-700"
          data-testid="fotografias-refazer-aguardando"
        >
          {t.aguardandoRecusa}
        </p>
      ) : null}
      {jaEmCurso && !emAndamento ? (
        <p
          role="status"
          className="text-body-sm text-accent-700"
          data-testid="fotografias-refazer-em-andamento"
        >
          {t.emAndamento}
        </p>
      ) : null}
    </div>
  );
}
